import { Fragment, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { flexRender, type Row, type Table as TanstackTable } from "@tanstack/react-table";
import classNames from "classnames";
import { Inbox } from "lucide-react";
import { Table as BaseTable, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "components/ui/table";
import { Spinner } from "components/ui/spinner";
import Pagination from "components/shared/Pagination";
import SortableHeader from "./SortableHeader";
import {
    buildRowSpanMaps,
    getRowSpanKeys,
    isGroupedContinuationRow,
    sortRowsByRowSpanKeys
} from "./helpers";
import { cn } from "lib/utils";
// import "./types";
import "./styles.scss";

interface TableProps<TData> {
    table: TanstackTable<TData>;
    children?: (row: Row<TData>) => ReactNode;
}

function Table<TData>({ table, children: renderSubComponent }: TableProps<TData>) {
    const { t } = useTranslation();
    const { columns } = table.options;
    const {
        loading,
        totalItems = 0,
        errors,
        footer,
        pageIndex = 0,
        pageSize = 10,
        onPageChange,
        onPageSizeChange,
        showSizeChanger,
        onRowClick,
    } = table.options.meta ?? {};
    const rawRows = table.getRowModel().rows;
    const rowSpanKeys = useMemo(() => getRowSpanKeys(columns), [columns]);
    const sortedRows = useMemo(() => sortRowsByRowSpanKeys(rawRows, rowSpanKeys), [rawRows, rowSpanKeys]);
    const rowSpanMaps = useMemo(() => buildRowSpanMaps(sortedRows, rowSpanKeys), [sortedRows, rowSpanKeys]);

    const hasPagination = !!(onPageChange && onPageSizeChange);

    return (
        <div className="tw-flex tw-flex-col tw-h-full tw-min-h-0">
            <div className={cn("table-scroll-container tw-rounded-lg tw-border tw-overflow-hidden", hasPagination && "tw-mb-5")}>
                <BaseTable>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="tw-h-13">
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} colSpan={header.colSpan} className="tw-m-0 tw-px-2 tw-h-14">
                                        {header.isPlaceholder ? null : header.column.getCanSort() ? (
                                            <SortableHeader column={header.column}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                            </SortableHeader>
                                        ) : (
                                            flexRender(header.column.columnDef.header, header.getContext())
                                        )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>

                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={999}>
                                    <div className="tw-flex tw-justify-center">
                                        <Spinner />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : totalItems === 0 ? (
                            <TableRow>
                                <TableCell colSpan={999}>
                                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-4 tw-text-gray-500 tw-px-0 tw-mx-0">
                                        <Inbox />
                                        {t("no-data")}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedRows.map((row) => {
                                const isContinuation = isGroupedContinuationRow(row, rowSpanMaps);
                                const rowError = errors?.[(row.original as { id: string })?.id];
                                const hasError = typeof rowError === "object" && rowError !== null
                                    ? Object.values(rowError).some(Boolean)
                                    : !!rowError;

                                return (
                                    <Fragment key={row.id}>
                                        <TableRow
                                            className={classNames(isContinuation && "grouped-row", "tw-relative tw-h-12", hasError && "tw-bg-rose-50 hover:tw-bg-rose-50", onRowClick && "tw-cursor-pointer")}
                                            onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                                        >
                                            {row.getVisibleCells().map((cell) => {
                                                const rowSpanKey = cell.column.columnDef.meta?.rowSpanBy;
                                                if (rowSpanKey) {
                                                    const info = rowSpanMaps[rowSpanKey]?.get(row.id);

                                                    return (
                                                        <TableCell
                                                            key={cell.id}
                                                            rowSpan={info?.span}
                                                            className={cn("grouped-cell tw-py-1 tw-align-middle tw-whitespace-nowrap", !info?.isFirst && "tw-hidden")}
                                                        >
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </TableCell>
                                                    );
                                                }

                                                return (
                                                    <TableCell key={cell.id} className="tw-py-1">
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>

                                        {row.getIsExpanded() && renderSubComponent && (
                                            <TableRow key={`${row.id}-expanded`}>
                                                <TableCell colSpan={row.getVisibleCells().length} className="tw-py-1">
                                                    {renderSubComponent(row)}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                );
                            })
                        )}
                    </TableBody>

                    {footer && (
                        <TableFooter>
                            {table.getFooterGroups().map((footerGroup) => (
                                <TableRow key={footerGroup.id}>
                                    {footerGroup.headers.map((header) => (
                                        <TableCell key={header.id} colSpan={header.colSpan}>
                                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableFooter>
                    )}
                </BaseTable>
            </div>

            {hasPagination && (
                <div className="print:tw-hidden">
                    <Pagination
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        onPageChange={onPageChange}
                        onPageSizeChange={onPageSizeChange}
                        showSizeChanger={showSizeChanger}
                    />
                </div>
            )}
        </div>
    );
}

export default Table;
